/**
 * Template Repository
 *
 * FERP CRITICAL: Repository pattern for Template primitive CRUD operations
 *
 * FERP Principles:
 * - Repository pattern (NO direct GraphQL calls outside this file)
 * - Backend ID generation (useGenerateId for template IDs)
 * - Data-driven (all templates from backend DynamoDB)
 * - Life-critical (complete error handling with user notifications)
 * - Domain-agnostic (works for all template types)
 * - Formula-aware (v1.1 - tracks formula participation)
 *
 * GraphQL Operations:
 * - getTemplate: Single template retrieval
 * - listTemplates: Query by tenant with pagination
 * - listTemplatesByStatus: Filter by status (Draft, Published, etc.)
 * - listTemplatesByFormula: Query by formula (GSI8) with success rate filtering
 * - getTemplateFormulaSuccessRate: Get success rate for template + formula
 * - createTemplate: Create new template with backend Snowflake ID
 * - updateTemplate: Update existing template (immutable audit)
 * - deleteTemplate: Soft delete (preserves audit trail)
 * - recordTemplateUsage: Update formula metrics (atomic operation)
 * - publishTemplate: Move template from Draft → Published
 */

import { ApolloClient, gql } from '@apollo/client';
import type {
  Template,
  TemplateStatus,
  TemplateConnection,
  CreateTemplateInput,
  UpdateTemplateInput,
  BusinessMetadata,
  UiMetadata,
  PrimitiveMetadata,
  MappingsMetadata,
  AuditInfo,
} from '../types/Template';
import type { BackendId } from '../types/branded-ids';

// ============================================================================
// GraphQL Fragments
// ============================================================================

const BUSINESS_METADATA_FRAGMENT = gql`
  fragment BusinessMetadataFields on BusinessMetadata {
    domain
    processName
    icon
    description
  }
`;

const UI_METADATA_FRAGMENT = gql`
  fragment UiMetadataFields on UiMetadata {
    layout
    steps {
      id
      label
      description
      fields {
        id
        fieldType
        label
        required
        placeholder
        helpText
        validationRules {
          ruleType
          parameters
          errorMessage
        }
      }
    }
  }
`;

const PRIMITIVE_METADATA_FRAGMENT = gql`
  fragment PrimitiveMetadataFields on PrimitiveMetadata {
    nodes {
      id
      primitiveType
      name
      configuration
    }
    connections {
      id
      sourceId
      targetId
      connectionType
    }
  }
`;

const MAPPINGS_METADATA_FRAGMENT = gql`
  fragment MappingsMetadataFields on MappingsMetadata {
    uiToPrimitive
    primitiveToUi
    transformFunctions
  }
`;

const AUDIT_INFO_FRAGMENT = gql`
  fragment AuditInfoFields on AuditInfo {
    createdBy
    createdAt
    updatedBy
    updatedAt
    version
  }
`;

const TEMPLATE_FRAGMENT = gql`
  fragment LegacyTemplateFields on Template {
    # Identity
    id
    tenantId
    name
    version
    description

    # Metadata DSL (4 layers)
    businessLayer {
      ...BusinessMetadataFields
    }
    uiLayer {
      ...UiMetadataFields
    }
    primitiveLayer {
      ...PrimitiveMetadataFields
    }
    mappingsLayer {
      ...MappingsMetadataFields
    }

    # Template Management
    status
    parentTemplateId
    category
    tags
    usageCount

    # Approval & Validation
    validationResult
    approvalWorkflowId
    publishedBy
    publishedAt
    confidenceScore

    # Regulatory & Compliance
    complianceFrameworks
    criticality
    authorityId
    complianceLevel
    auditRequirements
    jurisdictionalScope
    effectiveDate
    expirationDate
    riskClassification

    # Metadata & Extensibility
    metadata
    auditInfo {
      ...AuditInfoFields
    }
    active

    # Formula Tracking (v1.1)
    targetFormulas
    generatedInstanceIds
    formulaSuccessRates
    formulasParticipating
    lastGeneratedAt
    generationCount
  }
  ${BUSINESS_METADATA_FRAGMENT}
  ${UI_METADATA_FRAGMENT}
  ${PRIMITIVE_METADATA_FRAGMENT}
  ${MAPPINGS_METADATA_FRAGMENT}
  ${AUDIT_INFO_FRAGMENT}
`;

// ============================================================================
// GraphQL Queries
// ============================================================================

const GET_TEMPLATE = gql`
  query GetTemplate($id: ID!, $tenantId: String!) {
    getTemplate(id: $id, tenantId: $tenantId) {
      ...LegacyTemplateFields
    }
  }
  ${TEMPLATE_FRAGMENT}
`;

const LIST_TEMPLATES = gql`
  query ListTemplates($tenantId: String!, $limit: Int, $nextToken: String) {
    listTemplates(tenantId: $tenantId, limit: $limit, nextToken: $nextToken) {
      items {
        ...LegacyTemplateFields
      }
      nextToken
      totalCount
    }
  }
  ${TEMPLATE_FRAGMENT}
`;

const LIST_TEMPLATES_BY_STATUS = gql`
  query ListTemplatesByStatus(
    $tenantId: String!
    $status: TemplateStatus!
    $limit: Int
    $nextToken: String
  ) {
    listTemplatesByStatus(
      tenantId: $tenantId
      status: $status
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ...LegacyTemplateFields
      }
      nextToken
      totalCount
    }
  }
  ${TEMPLATE_FRAGMENT}
`;

const LIST_TEMPLATES_BY_FORMULA = gql`
  query ListTemplatesByFormula(
    $formulaId: String!
    $tenantId: String!
    $minSuccessRate: Float
    $limit: Int
  ) {
    listTemplatesByFormula(
      formulaId: $formulaId
      tenantId: $tenantId
      minSuccessRate: $minSuccessRate
      limit: $limit
    ) {
      ...LegacyTemplateFields
    }
  }
  ${TEMPLATE_FRAGMENT}
`;

const GET_TEMPLATE_FORMULA_SUCCESS_RATE = gql`
  query GetTemplateFormulaSuccessRate(
    $templateId: ID!
    $formulaId: String!
    $tenantId: String!
  ) {
    getTemplateFormulaSuccessRate(
      templateId: $templateId
      formulaId: $formulaId
      tenantId: $tenantId
    )
  }
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

const CREATE_TEMPLATE = gql`
  mutation CreateTemplate($input: CreateTemplateInput!) {
    createTemplate(input: $input) {
      ...LegacyTemplateFields
    }
  }
  ${TEMPLATE_FRAGMENT}
`;

const UPDATE_TEMPLATE = gql`
  mutation UpdateTemplate($input: UpdateTemplateInput!) {
    updateTemplate(input: $input) {
      ...LegacyTemplateFields
    }
  }
  ${TEMPLATE_FRAGMENT}
`;

const DELETE_TEMPLATE = gql`
  mutation DeleteTemplate($id: ID!, $tenantId: String!, $actor: String!) {
    deleteTemplate(id: $id, tenantId: $tenantId, actor: $actor)
  }
`;

const RECORD_TEMPLATE_USAGE = gql`
  mutation RecordTemplateUsage(
    $templateId: ID!
    $formulaId: String!
    $tenantId: String!
    $instanceId: ID!
    $success: Boolean!
  ) {
    recordTemplateUsage(
      templateId: $templateId
      formulaId: $formulaId
      tenantId: $tenantId
      instanceId: $instanceId
      success: $success
    ) {
      ...LegacyTemplateFields
    }
  }
  ${TEMPLATE_FRAGMENT}
`;

const PUBLISH_TEMPLATE = gql`
  mutation PublishTemplate(
    $templateId: ID!
    $tenantId: String!
    $publishedBy: String!
  ) {
    publishTemplate(
      templateId: $templateId
      tenantId: $tenantId
      publishedBy: $publishedBy
    ) {
      ...LegacyTemplateFields
    }
  }
  ${TEMPLATE_FRAGMENT}
`;

// ============================================================================
// Template Repository Class
// ============================================================================

export class TemplateRepository {
  constructor(private readonly apolloClient: ApolloClient) {}

  /**
   * Get template by ID
   *
   * FERP: Single template retrieval with complete metadata
   */
  async get(id: BackendId, tenantId: string): Promise<Template | null> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: GET_TEMPLATE,
      variables: { id, tenantId },
      fetchPolicy: 'network-only', // Always get fresh data
    });

    if (!data?.getTemplate) {
      return null;
    }

    return this.mapToFrontendTemplate(data?.getTemplate);
  }

  /**
   * List templates by tenant with pagination
   *
   * FERP: Returns all templates for a tenant
   */
  async listByTenant(
    tenantId: string,
    limit?: number,
    nextToken?: string
  ): Promise<TemplateConnection> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: LIST_TEMPLATES,
      variables: { tenantId, limit, nextToken },
      fetchPolicy: 'network-only',
    });

    if (!data?.listTemplates) {
      return { items: [], totalCount: 0 };
    }

    return {
      items: data?.listTemplates.items.map(this.mapToFrontendTemplate),
      nextToken: data?.listTemplates.nextToken,
      totalCount: data?.listTemplates.totalCount,
    };
  }

  /**
   * List templates by status
   *
   * FERP: Filter by status (Draft, Published, etc.)
   */
  async listByStatus(
    tenantId: string,
    status: TemplateStatus,
    limit?: number,
    nextToken?: string
  ): Promise<TemplateConnection> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: LIST_TEMPLATES_BY_STATUS,
      variables: { tenantId, status, limit, nextToken },
      fetchPolicy: 'network-only',
    });

    if (!data?.listTemplatesByStatus) {
      return { items: [], totalCount: 0 };
    }

    return {
      items: data?.listTemplatesByStatus.items.map(this.mapToFrontendTemplate),
      nextToken: data?.listTemplatesByStatus.nextToken,
      totalCount: data?.listTemplatesByStatus.totalCount,
    };
  }

  /**
   * List templates by formula (v1.1 - uses GSI8)
   *
   * FERP: Query templates optimized for specific formula pattern
   * Returns templates sorted by success rate (highest first)
   */
  async listByFormula(
    formulaId: string,
    tenantId: string,
    minSuccessRate?: number,
    limit?: number
  ): Promise<Template[]> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: LIST_TEMPLATES_BY_FORMULA,
      variables: { formulaId, tenantId, minSuccessRate, limit },
      fetchPolicy: 'network-only',
    });

    if (!data?.listTemplatesByFormula) {
      return [];
    }

    return data?.listTemplatesByFormula.map(this.mapToFrontendTemplate);
  }

  /**
   * Get template formula success rate (v1.1)
   *
   * FERP: Returns success rate 0.0-1.0 for template + formula combination
   */
  async getFormulaSuccessRate(
    templateId: BackendId,
    formulaId: string,
    tenantId: string
  ): Promise<number> {
    const { data } = await this.apolloClient.query<Record<string, any>>({
      query: GET_TEMPLATE_FORMULA_SUCCESS_RATE,
      variables: { templateId, formulaId, tenantId },
      fetchPolicy: 'network-only',
    });

    return data?.getTemplateFormulaSuccessRate ?? 0.0;
  }

  /**
   * Create template
   *
   * FERP: Backend generates Snowflake ID (5-layer enforcement prevents client-side)
   */
  async create(input: CreateTemplateInput): Promise<Template> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: CREATE_TEMPLATE,
      variables: { input },
    });

    if (!data?.createTemplate) {
      throw new Error('Failed to create template');
    }

    return this.mapToFrontendTemplate(data?.createTemplate);
  }

  /**
   * Update template
   *
   * FERP: Immutable audit trail (backend tracks updated_at/by)
   */
  async update(input: UpdateTemplateInput): Promise<Template> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: UPDATE_TEMPLATE,
      variables: { input },
    });

    if (!data?.updateTemplate) {
      throw new Error('Failed to update template');
    }

    return this.mapToFrontendTemplate(data?.updateTemplate);
  }

  /**
   * Delete template (soft delete)
   *
   * FERP: Preserves audit trail, never truly deletes data
   */
  async delete(id: BackendId, tenantId: string, actor: string): Promise<boolean> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: DELETE_TEMPLATE,
      variables: { id, tenantId, actor },
    });

    return data?.deleteTemplate ?? false;
  }

  /**
   * Record template usage (v1.1)
   *
   * FERP: Atomically updates formula metrics when template generates instance
   */
  async recordUsage(
    templateId: BackendId,
    formulaId: string,
    tenantId: string,
    instanceId: BackendId,
    success: boolean
  ): Promise<Template> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: RECORD_TEMPLATE_USAGE,
      variables: { templateId, formulaId, tenantId, instanceId, success },
    });

    if (!data?.recordTemplateUsage) {
      throw new Error('Failed to record template usage');
    }

    return this.mapToFrontendTemplate(data.recordTemplateUsage);
  }

  /**
   * Publish template
   *
   * FERP: Move template from Draft/PendingApproval → Published
   */
  async publish(
    templateId: BackendId,
    tenantId: string,
    publishedBy: string
  ): Promise<Template> {
    const { data } = await this.apolloClient.mutate<Record<string, any>>({
      mutation: PUBLISH_TEMPLATE,
      variables: { templateId, tenantId, publishedBy },
    });

    if (!data?.publishTemplate) {
      throw new Error('Failed to publish template');
    }

    return this.mapToFrontendTemplate(data.publishTemplate);
  }

  // ============================================================================
  // Private Mapping Functions (GraphQL → Frontend Types)
  // ============================================================================

  /**
   * Map GraphQL Template to frontend Template
   *
   * Converts camelCase GraphQL fields to match TypeScript interface
   */
  private mapToFrontendTemplate(gqlTemplate: any): Template {
    return {
      // Identity
      id: gqlTemplate.id as BackendId,
      tenantId: gqlTemplate.tenantId,
      name: gqlTemplate.name,
      version: gqlTemplate.version,
      description: gqlTemplate.description,

      // Metadata DSL (4 layers)
      businessLayer: this.mapBusinessMetadata(gqlTemplate.businessLayer),
      uiLayer: this.mapUiMetadata(gqlTemplate.uiLayer),
      primitiveLayer: this.mapPrimitiveMetadata(gqlTemplate.primitiveLayer),
      mappingsLayer: this.mapMappingsMetadata(gqlTemplate.mappingsLayer),

      // Template Management
      status: gqlTemplate.status as TemplateStatus,
      parentTemplateId: gqlTemplate.parentTemplateId as BackendId | undefined,
      category: gqlTemplate.category,
      tags: gqlTemplate.tags,
      usageCount: gqlTemplate.usageCount,

      // Approval & Validation
      validationResult: gqlTemplate.validationResult,
      approvalWorkflowId: gqlTemplate.approvalWorkflowId as BackendId | undefined,
      publishedBy: gqlTemplate.publishedBy,
      publishedAt: gqlTemplate.publishedAt,
      confidenceScore: gqlTemplate.confidenceScore,

      // Regulatory & Compliance
      complianceFrameworks: gqlTemplate.complianceFrameworks,
      criticality: gqlTemplate.criticality,
      authorityId: gqlTemplate.authorityId as BackendId | undefined,
      complianceLevel: gqlTemplate.complianceLevel,
      auditRequirements: gqlTemplate.auditRequirements,
      jurisdictionalScope: gqlTemplate.jurisdictionalScope,
      effectiveDate: gqlTemplate.effectiveDate,
      expirationDate: gqlTemplate.expirationDate,
      riskClassification: gqlTemplate.riskClassification,

      // Metadata & Extensibility
      metadata: gqlTemplate.metadata,
      auditInfo: this.mapAuditInfo(gqlTemplate.auditInfo),
      active: gqlTemplate.active,

      // Formula Tracking (v1.1)
      targetFormulas: gqlTemplate.targetFormulas,
      generatedInstanceIds: gqlTemplate.generatedInstanceIds as BackendId[],
      formulaSuccessRates: gqlTemplate.formulaSuccessRates,
      formulasParticipating: gqlTemplate.formulasParticipating,
      lastGeneratedAt: gqlTemplate.lastGeneratedAt,
      generationCount: gqlTemplate.generationCount,
    };
  }

  private mapBusinessMetadata(gqlMeta: any): BusinessMetadata {
    return {
      domain: gqlMeta.domain,
      processName: gqlMeta.processName,
      icon: gqlMeta.icon,
      description: gqlMeta.description,
    };
  }

  private mapUiMetadata(gqlMeta: any): UiMetadata {
    return {
      layout: gqlMeta.layout,
      steps: gqlMeta.steps,
    };
  }

  private mapPrimitiveMetadata(gqlMeta: any): PrimitiveMetadata {
    return {
      nodes: gqlMeta.nodes.map((node: { id: string; primitiveType: string; name: string; configuration: unknown }) => ({
        id: node.id as BackendId,
        primitiveType: node.primitiveType,
        name: node.name,
        configuration: node.configuration,
      })),
      connections: gqlMeta.connections.map((conn: { id: string; sourceId: string; targetId: string; connectionType: string }) => ({
        id: conn.id as BackendId,
        sourceId: conn.sourceId as BackendId,
        targetId: conn.targetId as BackendId,
        connectionType: conn.connectionType,
      })),
    };
  }

  private mapMappingsMetadata(gqlMeta: any): MappingsMetadata {
    return {
      uiToPrimitive: gqlMeta.uiToPrimitive,
      primitiveToUi: gqlMeta.primitiveToUi,
      transformFunctions: gqlMeta.transformFunctions,
    };
  }

  private mapAuditInfo(gqlAudit: any): AuditInfo {
    return {
      createdBy: gqlAudit.createdBy,
      createdAt: gqlAudit.createdAt,
      updatedBy: gqlAudit.updatedBy,
      updatedAt: gqlAudit.updatedAt,
      version: gqlAudit.version,
    };
  }
}

/**
 * Factory function to create TemplateRepository instance
 *
 * FERP: Use this in hooks/components to get repository instance
 */
export function createTemplateRepository(
  apolloClient: ApolloClient
): TemplateRepository {
  return new TemplateRepository(apolloClient);
}
